import { noop } from 'lodash';
import { useMemo, useSyncExternalStore } from 'react';

import { type AnnouncementSurface, pickAnnouncementForSurface } from '@air/feature-announcements';

import {
  type PrivateAnnouncementListItem,
  usePrivateAnnouncementsList,
} from '~/components/v2/hooks/queries/usePrivateAnnouncementsList';

export type UseActiveAnnouncementParams = {
  enabled?: boolean;
  surface: AnnouncementSurface;
};

const ANNOUNCEMENT_EXPIRY_TICK_MS = 1000;

let cachedNow = Date.now();

const subscribeToNow = (onStoreChange: () => void) => {
  // The clock only ticks while subscribed, so a surface that subscribes late would otherwise start stale.
  cachedNow = Date.now();
  const intervalId = window.setInterval(() => {
    cachedNow = Date.now();
    onStoreChange();
  }, ANNOUNCEMENT_EXPIRY_TICK_MS);

  return () => {
    window.clearInterval(intervalId);
  };
};

const subscribeToNothing = () => noop;

// useSyncExternalStore requires getSnapshot to return a value stable between calls;
// a freshly constructed Date looks "changed" on every render and loops forever.
const getNow = () => cachedNow;

export const useActiveAnnouncement = ({
  enabled = true,
  surface,
}: UseActiveAnnouncementParams): PrivateAnnouncementListItem | null => {
  const { data } = usePrivateAnnouncementsList();
  const announcements = data ?? [];
  const hasAnnouncementToExpire = enabled && announcements.length > 0;
  const now = useSyncExternalStore(hasAnnouncementToExpire ? subscribeToNow : subscribeToNothing, getNow, getNow);

  return useMemo(() => {
    if (!enabled) {
      return null;
    }

    return pickAnnouncementForSurface({
      announcements,
      now: new Date(now),
      surface,
    });
  }, [announcements, enabled, now, surface]);
};
